/**
 * A side column that scrolls to its own end before it sticks.
 *
 * `position: sticky` pins the *top* of an element, which is right until the element is
 * taller than the viewport — then its lower half is permanently below the fold and the
 * only way to reach it is to scroll the whole page to the bottom, which on a long
 * ledger means scrolling past everything to read a panel that was meant to stay in
 * view. Pinning the top is the one behaviour that cannot show you a tall column.
 *
 * So when it does not fit, the offset is set so its *bottom* rests against the bottom
 * of the viewport instead. It then travels with the page until you have seen all of it
 * and stays put from there, which is what people mean by a sticky sidebar.
 *
 * A column that fits gets exactly the offset it always had, so this is a no-op
 * everywhere the problem does not exist.
 */

import { type RefObject, useEffect, useRef } from 'react'

/** Clears the app header. Matches the `top-[96px]` these columns are written with. */
const TOP_GAP = 96
/** So the last row is not flush against the window edge. */
const BOTTOM_GAP = 20

export function useStickyColumn<T extends HTMLElement>(): RefObject<T | null> {
  const ref = useRef<T>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const measure = () => {
      const overflows = element.offsetHeight > window.innerHeight - TOP_GAP - BOTTOM_GAP
      element.style.top = overflows
        ? // Negative once the column is taller than the window, which is what lets it
          // keep travelling upward until its end appears.
          `${window.innerHeight - element.offsetHeight - BOTTOM_GAP}px`
        : `${TOP_GAP}px`
    }

    measure()
    // The column changes height as panels appear — "money you could move" shows up only
    // when there is some — so watching it beats measuring once on mount.
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    window.addEventListener('resize', measure)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [])

  return ref
}

/**
 * A panel that scrolls inside itself, fitted to whatever of the window it can see.
 *
 * The envelope editor wants the opposite of the above: it is a form, so it should stay
 * put and scroll its own content rather than travel with the page. That needs a height,
 * and the height it had — `100vh - 116px` — assumed it was already pinned 96px from the
 * top. It is not, until you have scrolled far enough for sticky to engage: before that
 * it sits wherever the grid puts it, a couple of hundred pixels lower, and a full-height
 * panel runs off the bottom of the window. Its own scrollbar reaches the end while the
 * last inch is still below the fold, which reads as a panel that will not scroll.
 *
 * So the height comes from where the panel actually is, re-measured as the page moves.
 * Once sticky engages this settles at exactly the old figure.
 */
export function useFittedPanel<T extends HTMLElement>(): RefObject<T | null> {
  const ref = useRef<T>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    /*
     * Only where the panel is actually a column. Below `xl` it is stacked in the page
     * flow and capping its height would put a scrollbar inside a scrollbar — the panel
     * is not pinned there, so the page scroll already reaches its bottom.
     */
    const isColumn = window.matchMedia(`(min-width: ${XL}px)`)

    let queued = false
    const measure = () => {
      queued = false
      if (!isColumn.matches) {
        element.style.maxHeight = ''
        return
      }
      const top = element.getBoundingClientRect().top
      element.style.maxHeight = `${Math.max(MIN_PANEL, window.innerHeight - top - BOTTOM_GAP)}px`
    }
    // Every scroll frame would re-measure otherwise, and this reads layout.
    const onScroll = () => {
      if (queued) return
      queued = true
      requestAnimationFrame(measure)
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', measure)
    isColumn.addEventListener('change', measure)
    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', measure)
      isColumn.removeEventListener('change', measure)
    }
  }, [])

  return ref
}

/** Below this the panel is too short to be worth scrolling inside; let the page take over. */
const MIN_PANEL = 320

/** Tailwind's `xl`, the width at which these become side columns rather than stacked. */
const XL = 1280
